import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableDrillRow({
  row,
  disabled,
  ackList,
  expanded,
  onToggleExpand,
  chalkTalkAckersLoading,
  parentLabel,
  formatAckTime,
  onEdit,
  onMoveToLibrary,
  onRequestRemove,
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 2 : 0,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className="chalk-admin-drill-row chalk-admin-drill-grid">
      <div className="chalk-admin-drill-cell chalk-admin-drill-cell--drag">
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="chalk-admin-drag-handle"
          aria-label={`Drag to reorder: ${row.name || 'drill'}`}
          disabled={disabled}
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
      </div>
      <div className="chalk-admin-drill-cell chalk-admin-drill-cell--name" title={row.name || ''}>
        <span className="chalk-admin-drill-order-num">{Number(row.order) || 0}</span>
        {row.name || '—'}
      </div>
      <div className="chalk-admin-drill-cell">
        {row.videoUrl || row.link ? (
          <a href={row.videoUrl || row.link} target="_blank" rel="noopener noreferrer" style={{ color: '#00a7a9' }}>
            link
          </a>
        ) : (
          '—'
        )}
      </div>
      <div className="chalk-admin-drill-cell">
        {row.imageUrl ? (
          <a href={row.imageUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#00a7a9' }}>
            view
          </a>
        ) : (
          '—'
        )}
      </div>
      <div className="chalk-admin-drill-cell chalk-admin-drill-cell--notes">
        {(row.notes || '').slice(0, 80)}
        {(row.notes || '').length > 80 ? '…' : ''}
      </div>
      <div className="chalk-admin-drill-cell chalk-admin-drill-cell--acks">
        {chalkTalkAckersLoading ? (
          '…'
        ) : ackList.length === 0 ? (
          '—'
        ) : (
          <>
            <button
              type="button"
              onClick={() => onToggleExpand(row.id)}
              className="chalk-admin-ack-toggle"
            >
              {ackList.length} parent{ackList.length === 1 ? '' : 's'}
            </button>
            {expanded ? (
              <ul className="chalk-admin-ack-list">
                {ackList.map(({ parentId, at }) => (
                  <li key={parentId}>
                    {parentLabel(parentId)}
                    {at ? <span className="chalk-admin-ack-time"> — {formatAckTime(at)}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
      <div className="chalk-admin-drill-cell chalk-admin-drill-cell--actions">
        <button type="button" className="admin-button" onClick={() => onEdit(row)}>
          Edit
        </button>{' '}
        <button
          type="button"
          className="admin-button"
          title="Remove from this practice and save to your drill library"
          disabled={disabled}
          onClick={() => onMoveToLibrary(row)}
        >
          Move to library
        </button>{' '}
        <button type="button" className="admin-button-archive" onClick={() => onRequestRemove(row)}>
          Delete…
        </button>
      </div>
    </div>
  );
}

function ChalkTalkDrillDayBlock({
  practiceDateKey,
  rows,
  onReorderDay,
  disabled,
  chalkTalkDrillAckers,
  chalkTalkAckersLoading,
  chalkTalkExpandedAckDrillId,
  setChalkTalkExpandedAckDrillId,
  parents,
  onEdit,
  onMoveToLibrary,
  onRequestRemove,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const formatAckTime = (at) => {
    if (!at) return '';
    const dt =
      typeof at.toDate === 'function'
        ? at.toDate()
        : at.seconds
          ? new Date(at.seconds * 1000)
          : new Date(at);
    return dt.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  };

  const parentLabel = (uid) => {
    const p = parents.find((x) => x.id === uid);
    if (!p) return uid;
    const n = (p.name || p.displayName || '').trim();
    const e = (p.email || '').trim();
    if (n && e) return `${n} (${e})`;
    return n || e || uid;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(rows, oldIndex, newIndex);
    onReorderDay(reordered.map((r) => r.id));
  };

  const ids = rows.map((r) => r.id);

  return (
    <div className="chalk-admin-day-block">
      <h4 className="chalk-admin-day-title">Practice — {practiceDateKey}</h4>
      <p className="chalk-admin-day-hint">Drag the handle (⠿) to reorder drills for this practice.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="chalk-admin-drill-grid chalk-admin-drill-grid--head">
            <div className="chalk-admin-drill-cell chalk-admin-drill-cell--drag" aria-hidden />
            <div className="chalk-admin-drill-cell">Name</div>
            <div className="chalk-admin-drill-cell">Video</div>
            <div className="chalk-admin-drill-cell">Image</div>
            <div className="chalk-admin-drill-cell">Notes</div>
            <div className="chalk-admin-drill-cell">Reviewed by</div>
            <div className="chalk-admin-drill-cell">Actions</div>
          </div>
          {rows.map((row) => (
            <SortableDrillRow
              key={row.id}
              row={row}
              disabled={disabled}
              ackList={chalkTalkDrillAckers[row.id] || []}
              expanded={chalkTalkExpandedAckDrillId === row.id}
              onToggleExpand={(id) =>
                setChalkTalkExpandedAckDrillId((cur) => (cur === id ? null : id))
              }
              chalkTalkAckersLoading={chalkTalkAckersLoading}
              parentLabel={parentLabel}
              formatAckTime={formatAckTime}
              onEdit={onEdit}
              onMoveToLibrary={onMoveToLibrary}
              onRequestRemove={onRequestRemove}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

export default function ChalkTalkDrillsAdminList({
  groups,
  onReorderDay,
  disabled,
  chalkTalkDrillAckers,
  chalkTalkAckersLoading,
  chalkTalkExpandedAckDrillId,
  setChalkTalkExpandedAckDrillId,
  parents,
  onEdit,
  onMoveToLibrary,
  onRequestRemove,
}) {
  return (
    <div className="chalk-admin-drills-wrap">
      {groups.map(({ practiceDateKey, rows }) => (
        <ChalkTalkDrillDayBlock
          key={practiceDateKey}
          practiceDateKey={practiceDateKey}
          rows={rows}
          onReorderDay={onReorderDay}
          disabled={disabled}
          chalkTalkDrillAckers={chalkTalkDrillAckers}
          chalkTalkAckersLoading={chalkTalkAckersLoading}
          chalkTalkExpandedAckDrillId={chalkTalkExpandedAckDrillId}
          setChalkTalkExpandedAckDrillId={setChalkTalkExpandedAckDrillId}
          parents={parents}
          onEdit={onEdit}
          onMoveToLibrary={onMoveToLibrary}
          onRequestRemove={onRequestRemove}
        />
      ))}
    </div>
  );
}
